import { Dayjs } from "dayjs";
import { SeriesOptionsType } from "highcharts";
import { Seller, TransactionType } from "types";
import { api } from "../api/api";
import { config } from "../config";
import { AnalyticsMode } from "../stores/reports/analyticsFilterStore";
import { getSourcePlugin } from "@/components/Common/SourceIcon";

// Interface for the params
export interface PaymentAndChannelsReportParams {
    seller: Seller;
    from: Dayjs;
    to: Dayjs;
    terms: string;
    stats: string;
    mode: AnalyticsMode;
    products: ID[];
    getName?: (key: any) => string;
    plugins: any[];
}

export const getPaymentsReport = async (params: PaymentAndChannelsReportParams): Promise<SeriesOptionsType[]> => {
    const { seller, getName } = params;
    const index = config.elasticsearch.indexes.transaction;
    const { data } = await api.post(`/elasticsearch/${index}/_search?seller=${seller.id}`, getPayload(params));
    return getPaymentsResponse(data, getName!);
};

export const getChannelsReport = async (params: PaymentAndChannelsReportParams): Promise<SeriesOptionsType[]> => {
    const { seller, getName, plugins } = params;
    const index = config.elasticsearch.indexes.transaction;
    const { data } = await api.post(`/elasticsearch/${index}/_search?seller=${seller.id}`, getPayload(params));
    return getChannelsResponse(data, getName!, plugins);
};

const getPayload = ({ seller, from, to, terms, stats, mode, products }: PaymentAndChannelsReportParams) => {
    const types: TransactionType[] = [
        TransactionType.PURCHASE,
        TransactionType.DEPOSIT,
        TransactionType.BALANCE,
        TransactionType.PURCHASE_AFFILIATE,
    ];

    const filter = getBaseFilter(seller, terms, stats, types);

    const transactionFilter = filter.query.bool.must;
    const itemsFilter = transactionFilter[0].nested.query.bool.must;
    const itemsAggsFilter = filter.aggs.pie_aggregation.aggs.items.aggs.filtered.filter.bool.must;
    const range = { from: from.valueOf(), to: to.valueOf() };
    if (mode === AnalyticsMode.CASH_FLOW) {
        transactionFilter.push({ range: { createdAt: range } });
    } else {
        const rangeFilter = { range: { "items.realizedAt": range } };
        itemsFilter.push(rangeFilter);
        itemsAggsFilter.push(rangeFilter);
    }

    if (products.length > 0) {
        const productFilter = { terms: { "items.orderItem.product.id": products } };
        itemsFilter.push(productFilter);
        itemsAggsFilter.push(productFilter);
    }

    return filter;
};

export const getListingsReport = async (params: PaymentAndChannelsReportParams): Promise<SeriesOptionsType[]> => {
    const { seller, getName } = params;
    const index = config.elasticsearch.indexes.transaction;
    const { data } = await api.post(`/elasticsearch/${index}/_search?seller=${seller.id}`, getListingsPayload(params));
    return getListingsResponse(data, getName!);
};

const getListingsPayload = (params: PaymentAndChannelsReportParams) => {
    const { terms } = params;
    const filter = getPayload(params);
    return {
        size: 0, // Don't show hits
        aggs: {
            pie_aggregation: {
                nested: { path: "items" },
                aggs: {
                    items: {
                        terms: { field: terms, size: 1_000_000 }, // TODO: SET MAX_RESULTS
                        aggs: filter.aggs.pie_aggregation.aggs.items.aggs,
                    },
                },
            },
        },
        query: filter.query,
    };
};

const getBaseFilter = (seller: Seller, termField: string, statsField: string, types: TransactionType[]) => {
    return {
        size: 0, // Don't show hits
        aggs: {
            pie_aggregation: {
                terms: { field: termField },
                aggs: {
                    items: {
                        nested: { path: "items" },
                        aggs: {
                            filtered: {
                                filter: { bool: { must: [] } },
                                aggs: { amount_stats: { stats: { field: statsField } } },
                            },
                        },
                    },
                },
            },
        },
        query: {
            bool: {
                must: [
                    { nested: { path: "items", query: { bool: { must: [] } } } },
                    { terms: { type: types } },
                    { term: { "seller.id": seller.id } },
                ],
            },
        },
    } as any;
};

const getPaymentsResponse = (data: any, getName: (key: any) => string) => {
    if (!data.aggregations) {
        console.error("Aggregations not found");
        return;
    }

    const results = data.aggregations.pie_aggregation.buckets;

    return results.map((r: any) => {
        const term = getName(r.key);
        return [term, Math.abs(r.items.filtered.amount_stats.sum)];
    });
};

const getChannelsResponse = (data: any, getName: (key: any) => string, plugins: any[]) => {
    if (!data.aggregations) {
        console.error("Aggregations not found");
        return;
    }

    const results = data.aggregations.pie_aggregation.buckets;

    return results.map((r: any) => {
        let term = getName(r.key);
        const pluginSource = getSourcePlugin(plugins, term);
        term = pluginSource ? pluginSource.name : term;
        return [term, Math.abs(r.items.filtered.amount_stats.sum)];
    });
};

const getListingsResponse = (data: any, getName: (id: ID) => string) => {
    if (!data.aggregations) {
        console.error("Aggregations not found");
        return;
    }

    const results = data.aggregations.pie_aggregation.items.buckets;
    return results.map((r: any) => [getName(r.key), r.filtered.amount_stats.sum]);
};
